import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static targets = [
    "button",
    "fieldLoader",
    "searchInput",
    "submitButton",
    "includedContinents",
    "excludedContinents",
    "includedBusinessRegions",
    "excludedBusinessRegions",
    "includedCountries",
    "excludedCountries",
    "includedStates",
    "excludedStates",
    "includedCities",
    "excludedCities",
    "dropdownOptionTemplate",
    "dropdownList",
    "dropdownOption",
    "selectedOptions",
    "selectedOption",
    "selectedOptionTemplate",
    "selectedOptionsCountTemplate",
  ]

  connect() {
    this.draftValues = this.#getSelectedOptions()
    this.#setUpSearchInput()
    this.#renderDropdownOptions()
    this.#renderSelectedOptions()
    this.#refreshDataCount()
  }

  disconnect() {
    this.#cleanUpSearchInput()
  }

  clear() {
    this.#getHiddenSelects().forEach(selectElement => {
      selectElement.innerHTML = "";
    })

    this.draftValues = []
    this.dropdownListTarget.innerHTML = ""
    this.selectedOptionsTarget.innerHTML = ""
    this.#refreshDataCount()
  }

  remove({ params: { value, type, included } }) {
    const selectElement = this.#getHiddenSelect(type, included)
    const optionIndex = Array.from(selectElement.options).findIndex(option => option.value === value)

    if (optionIndex > -1) {
      selectElement.options.remove(optionIndex)
    }

    this.#removeDropdownOption(value, type, included)
    this.#removeDraftOption(value, type)

    if (optionIndex === -1) {
      return
    }

    this.#renderSelectedOptions()
    this.#refreshDataCount()
    this.#dispatchFilterEvent()
  }

  apply() {
    this.draftValues.forEach(value => {
      const option = new Option(value.label, value.value, true, true)
      const selectElement = this.#getHiddenSelect(value.type, value.included)

      if (! Array.from(selectElement.options).some(opt => opt.value === value.value)) {
        selectElement.options.add(option)
      }
    })

    this.draftValues = this.#getSelectedOptions()
    this.submitButtonTarget.disabled = true
    this.#renderSelectedOptions()
    this.#dispatchFilterEvent()
    this.#refreshDataCount()
  }

  changeDropdownOption({ detail: { tagValue, included, dataset } }) {

    this.#removeDropdownOption(tagValue, dataset.type, !included)
    this.#removeDraftOption(tagValue, dataset.type)
    this.#addDraftOption(tagValue, dataset.label, dataset.type, included)
    this.submitButtonTarget.disabled = false
  }

  removeDropdownOption({ detail: { tagValue, included, dataset } }) {
    this.#removeDropdownOption(tagValue, dataset.type, included)
    this.#removeDraftOption(tagValue, dataset.type)
    this.submitButtonTarget.disabled = false
  }

  #setUpSearchInput() {
    this.runningRequests = 0

    this.searchInputSelectize = $(this.searchInputTarget).selectize({
      valueField: "value",
      labelField: "label",
      searchField: "label",
      optgroupField: "type",
      optgroups: [
        { value: "continent", label: "Continent" },
        { value: "business_region", label: "Business region" },
        { value: "country", label: "Country" },
        { value: "state", label: "State" },
        { value: "city", label: "City" },
      ],
      persist: false,
      create: false,
      copyClassesToDropdown: false,
      sortField: [{ field: "label" }],
      load: (query, callback) => {
        if (!query.length) {
          return callback()
        }

        // Show the spinner
        this.runningRequests += 1
        this.fieldLoaderTarget.classList.remove("d-none")

        fetch(`/locations?query=${encodeURIComponent(query)}`, {
          method: "GET",
          headers: { "Accept": "application/json" }
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            return response.json()
          })
          .then(data => {
            data = data.map(item => ({
              ...item,
              value: `${item.type}-${item.value}`
            }))

            callback(data)
          })
          .catch(error => {
            callback()
            console.error(error)
          })
          .finally(() => {
            this.runningRequests -= 1

            if (this.runningRequests <= 0) {
              // Hide the spinner, but only if there are no pending requests
              this.fieldLoaderTarget.classList.add("d-none")
            }
          })
      },
      onItemAdd: (compositeValue) => {
        // Extract the original type and value from the composite value
        const [, type, value] = compositeValue.match(/^([^-]+)-(.+)$/)
        let option = this.searchInputSelectize.options[compositeValue]
        this.#addDraftOption(value, option.label, type, true)
        this.submitButtonTarget.disabled = false
        this.#refreshSearchInput()
      },
    })[0].selectize
  }

  #cleanUpSearchInput() {
    this.searchInputSelectize?.destroy()
  }

  // Refresh selectize, clear the selected values and close it
  #refreshSearchInput() {
    this.searchInputSelectize.refreshOptions(false)
    this.searchInputSelectize.clear()
    this.searchInputSelectize.close()
  }

  #addDraftOption(value, label, type, included) {
    const location = { value: value, label: label, type: type, included: included }
    this.draftValues.push(location)
    this.#renderDropdownOption(value, label, type, included)
    this.#refreshDataCount()
  }

  #removeDraftOption(value, type) {
    this.draftValues = this.draftValues.filter(loc => loc.value !== value || loc.type !== type)
    this.#refreshDataCount()
  }

  #getHiddenSelects() {
    return [
      this.includedContinentsTarget,
      this.excludedContinentsTarget,
      this.includedBusinessRegionsTarget,
      this.excludedBusinessRegionsTarget,
      this.includedCountriesTarget,
      this.excludedCountriesTarget,
      this.includedStatesTarget,
      this.excludedStatesTarget,
      this.includedCitiesTarget,
      this.excludedCitiesTarget,
    ]
  }

  #getHiddenSelect(type, included) {
    switch (type) {
      case "continent":
        return included ? this.includedContinentsTarget : this.excludedContinentsTarget;
      case "business_region":
        return included ? this.includedBusinessRegionsTarget : this.excludedBusinessRegionsTarget;
      case "country":
        return included ? this.includedCountriesTarget : this.excludedCountriesTarget;
      case "state":
        return included ? this.includedStatesTarget : this.excludedStatesTarget;
      case "city":
        return included ? this.includedCitiesTarget : this.excludedCitiesTarget;
      default:
        throw new Error(`Unknown location type: ${type}`);
    }
  }

  #renderSelectedOptions() {
    this.selectedOptionsTarget.innerHTML = ""
    const selectedOptions = this.#getSelectedOptions()

    if (selectedOptions.length == 0) {
      return
    }

    if (selectedOptions.length < 3) {
      selectedOptions.forEach(option => {
        const clone = this.selectedOptionTemplateTarget.content.cloneNode(true)
        const element = clone.firstElementChild

        let label = option.label
        if (! option.included) {
          label = `${i18n.t("js.not")} ${label}`
        }

        element.innerHTML = element.innerHTML.replaceAll("$LABEL", label)
        element.innerHTML = element.innerHTML.replaceAll("$VALUE", option.value)
        element.innerHTML = element.innerHTML.replaceAll("$TYPE", option.type)
        element.innerHTML = element.innerHTML.replaceAll("$INCLUDED", option.included)
        this.selectedOptionsTarget.append(element)
      })
    } else {
      const clone = this.selectedOptionsCountTemplateTarget.content.cloneNode(true)

      const element = clone.firstElementChild
      element.innerHTML = element.innerHTML.replaceAll("$LABEL", `${selectedOptions.length} options`)
      this.selectedOptionsTarget.append(element)
    }
  }

  #renderDropdownOptions() {
    this.dropdownListTarget.innerHTML = ""
    this.draftValues.forEach(option => {
      this.#renderDropdownOption(option.value, option.label, option.type, option.included)
    })
  }

  #getSelectedOptions() {
    return this.#getSelectedOptionsFrom(this.includedContinentsTarget, "continent", true)
               .concat(this.#getSelectedOptionsFrom(this.excludedContinentsTarget, "continent", false))
               .concat(this.#getSelectedOptionsFrom(this.includedBusinessRegionsTarget, "business_region", true))
               .concat(this.#getSelectedOptionsFrom(this.excludedBusinessRegionsTarget, "business_region", false))
               .concat(this.#getSelectedOptionsFrom(this.includedCountriesTarget, "country", true))
               .concat(this.#getSelectedOptionsFrom(this.excludedCountriesTarget, "country", false))
               .concat(this.#getSelectedOptionsFrom(this.includedStatesTarget, "state", true))
               .concat(this.#getSelectedOptionsFrom(this.excludedStatesTarget, "state", false))
               .concat(this.#getSelectedOptionsFrom(this.includedCitiesTarget, "city", true))
               .concat(this.#getSelectedOptionsFrom(this.excludedCitiesTarget, "city", false))
  }

  #getSelectedOptionsFrom(selectElement, type, included) {
    return Array.from(selectElement.selectedOptions).map(option => {
      return { value: option.value, label: option.innerText, type: type, included: included }
    });
  }

  #getSelectedOptionValuesFrom(selectElement) {
    return Array.from(selectElement.selectedOptions).map(option => {
      return option.value
    });
  }

  #renderDropdownOption(value, label, type, included) {
    let tagHtml = this.dropdownOptionTemplateTarget.innerHTML
    tagHtml = tagHtml.replaceAll("$LABEL", label)
    tagHtml = tagHtml.replaceAll("$VALUE", value)
    tagHtml = tagHtml.replaceAll("$TYPE", type)
    tagHtml = tagHtml.replaceAll("$INCLUDED", included)

    // Show tooltip for long label as they are truncated
    if (label.length > 22) {
      tagHtml = tagHtml.replace("$TOOLTIP", label)
    } else {
      tagHtml = tagHtml.replace("$TOOLTIP", "")
    }

    let tagNode = document.createElement("div")
    tagNode.innerHTML = tagHtml

    this.dropdownListTarget.append(tagNode.firstElementChild)
  }

  #removeDropdownOption(value, type, included) {
    const selectElement = this.#getHiddenSelect(type, included)
    const index = Array.from(selectElement.options).findIndex((option) => {
      return option.value === value
    })

    this.dropdownOptionTargets.forEach(option => {
      if (option.dataset.value === value && option.dataset.type === type) {
        option.remove()
      }
    })

    selectElement.options.remove(index)
  }

  #refreshDataCount() {
    if (this.hasButtonTarget) {
      this.buttonTarget.dataset.selectedFilters = this.#getSelectedOptions().length
    }
  }

  #dispatchFilterEvent() {
    let filters = [
      { name: "location_included_continents", values: this.#getSelectedOptionValuesFrom(this.includedContinentsTarget)},
      { name: "location_excluded_continents", values: this.#getSelectedOptionValuesFrom(this.excludedContinentsTarget)},
      { name: "location_included_business_regions", values: this.#getSelectedOptionValuesFrom(this.includedBusinessRegionsTarget)},
      { name: "location_excluded_business_regions", values: this.#getSelectedOptionValuesFrom(this.excludedBusinessRegionsTarget)},
      { name: "location_included_countries", values: this.#getSelectedOptionValuesFrom(this.includedCountriesTarget)},
      { name: "location_excluded_countries", values: this.#getSelectedOptionValuesFrom(this.excludedCountriesTarget)},
      { name: "location_included_states", values: this.#getSelectedOptionValuesFrom(this.includedStatesTarget)},
      { name: "location_excluded_states", values: this.#getSelectedOptionValuesFrom(this.excludedStatesTarget)},
      { name: "location_included_cities", values: this.#getSelectedOptionValuesFrom(this.includedCitiesTarget)},
      { name: "location_excluded_cities", values: this.#getSelectedOptionValuesFrom(this.excludedCitiesTarget)},
    ]

    this.dispatch("filter", { detail: { filters: filters } })
  }
}
